import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "dropdown-content" }
const _hoisted_2 = {
  key: 0,
  class: "selected-value"
}
const _hoisted_3 = {
  key: 1,
  class: "placeholder"
}
const _hoisted_4 = { class: "dropdown-actions" }
const _hoisted_5 = { class: "dropdown-items" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "item-text" }
const _hoisted_8 = {
  key: 0,
  class: "pi pi-check item-check"
}
const _hoisted_9 = {
  key: 0,
  class: "dropdown-empty"
}

import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';

interface Option {
  label: string;
  value: any;
  [key: string]: any;
}

interface Props {
  modelValue?: string | number | null;
  options?: Option[];
  optionLabel?: string;
  optionValue?: string;
  placeholder?: string;
  showClear?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomDropdown',
  props: {
    modelValue: { default: null },
    options: { default: () => [] },
    optionLabel: { default: 'label' },
    optionValue: { default: 'value' },
    placeholder: { default: 'Select an option' },
    showClear: { type: Boolean, default: true }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

const emit = __emit;

const dropdownRef = ref(null);
const triggerRef = ref(null);
const panelRef = ref(null);
const isOpen = ref(false);
const panelStyle = ref({});

const selectedOption = computed((): Option | undefined => {
  if (!props.modelValue) return undefined;
  return props.options.find((option: Option) => option[props.optionValue] === props.modelValue);
});

const updatePanelPosition = async (): Promise<void> => {
  if (!triggerRef.value || !isOpen.value) return;
  
  await nextTick();
  
  const triggerRect = (triggerRef.value as HTMLElement).getBoundingClientRect();
  const scrollY = window.scrollY || window.pageYOffset;
  const scrollX = window.scrollX || window.pageXOffset;
  
  panelStyle.value = {
    position: 'absolute',
    top: `${triggerRect.bottom + scrollY + 8}px`,
    left: `${triggerRect.left + scrollX}px`,
    width: `${triggerRect.width}px`,
    zIndex: '99999'
  };
};

const toggleDropdown = async (): Promise<void> => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await updatePanelPosition();
  }
};

const selectOption = (option: Option): void => {
  const value = option[props.optionValue];
  emit('update:modelValue', value);
  emit('change', { value, originalEvent: null });
  isOpen.value = false;
};

const clearSelection = (): void => {
  emit('update:modelValue', null);
  emit('change', { value: null, originalEvent: null });
};

const closeDropdown = (event: Event): void => {
  if (dropdownRef.value && !(dropdownRef.value as HTMLElement).contains(event.target as Node) && 
      panelRef.value && !(panelRef.value as HTMLElement).contains(event.target as Node)) {
    isOpen.value = false;
  }
};

const handleResize = (): void => {
  if (isOpen.value) {
    updatePanelPosition();
  }
};

const handleScroll = (): void => {
  if (isOpen.value) {
    updatePanelPosition();
  }
};

// Watch for position updates when panel opens
watch(isOpen, (newValue: boolean) => {
  if (newValue) {
    updatePanelPosition();
  }
});

onMounted(() => {
  document.addEventListener('click', closeDropdown);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: "custom-dropdown",
      ref_key: "dropdownRef",
      ref: dropdownRef
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["dropdown-trigger", { 'is-open': isOpen.value, 'has-value': _ctx.modelValue }]),
        onClick: toggleDropdown,
        ref_key: "triggerRef",
        ref: triggerRef
      }, [
        _createElementVNode("div", _hoisted_1, [
          (selectedOption.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(selectedOption.value[_ctx.optionLabel]), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_ctx.placeholder), 1))
        ]),
        _createElementVNode("div", _hoisted_4, [
          (_ctx.showClear && _ctx.modelValue)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                onClick: _withModifiers(clearSelection, ["stop"]),
                class: "clear-button",
                type: "button"
              }, [...(_cache[0] || (_cache[0] = [
                _createElementVNode("i", { class: "pi pi-times" }, null, -1)
              ]))]))
            : _createCommentVNode("", true),
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "dropdown-arrow" }, [
            _createElementVNode("i", { class: "pi pi-chevron-down" })
          ], -1))
        ])
      ], 2)
    ], 512),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "dropdown-slide" }, {
        default: _withCtx(() => [
          (isOpen.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "dropdown-panel-portal",
                style: _normalizeStyle(panelStyle.value),
                ref_key: "panelRef",
                ref: panelRef
              }, [
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: option[_ctx.optionValue],
                      class: _normalizeClass(["dropdown-item", { 'is-selected': _ctx.modelValue === option[_ctx.optionValue] }]),
                      onClick: ($event: any) => (selectOption(option))
                    }, [
                      _createElementVNode("span", _hoisted_7, _toDisplayString(option[_ctx.optionLabel]), 1),
                      (_ctx.modelValue === option[_ctx.optionValue])
                        ? (_openBlock(), _createElementBlock("i", _hoisted_8))
                        : _createCommentVNode("", true)
                    ], 10, _hoisted_6))
                  }), 128)),
                  (_ctx.options.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, " No options available "))
                    : _createCommentVNode("", true)
                ])
              ], 4))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ], 64))
}
}

})